
import apiClient from './client'
import type { PoolTable } from '../types/table.types'
import type { GameSession } from '../types/session.types'

export const fetchTables = async (): Promise<PoolTable[]> => {
    const response = await apiClient.get('/tables')
    return response.data.data
}

export const fetchTableDetails = async (tableId: number): Promise<PoolTable> => {
    const response = await apiClient.get(`/tables/${tableId}`)
    return response.data.data
}

export const fetchTableSession = async (tableId: number): Promise<GameSession> => {
    const response = await apiClient.get(`/tables/${tableId}/session`)
    return response.data.data
}

export const callWaiter = async (tableId: number): Promise<void> => {
    await apiClient.post(`/tables/${tableId}/waiter`)
}

export const startTable = async (
    tableId: number,
    durationMinutes: number,
    players: Array<{ userId?: number; guestName?: string }>,
    tariffId?: number | null,
    pauseAfterDuration?: boolean,
    withTrainer?: boolean,
    videoRecordingEnabled?: boolean
): Promise<void> => {
    const response = await apiClient.post(`/tables/${tableId}/start`, {
        durationMinutes,
        players,
        tariffId: tariffId ?? null,
        pauseAfterDuration: pauseAfterDuration ?? false,
        withTrainer: withTrainer ?? false,
        videoRecordingEnabled: videoRecordingEnabled ?? false,
    })
    return response.data.data
}

export const finishSet = async (
    tableId: number,
    payerAssignmentIds?: number[]
): Promise<void> => {
    const response = await apiClient.post(`/tables/${tableId}/finishSet`, {
        payerAssignmentIds
    })
    return response.data.data
}

export const stopTable = async (
    tableId: number,
    payerAssignmentIds?: number[],
    lateVideoRecording?: boolean
): Promise<void> => {
    const response = await apiClient.post(`/tables/${tableId}/stop`, {
        payerAssignmentIds,
        lateVideoRecording: lateVideoRecording ?? false,
    })
    return response.data.data
}

export const setVideoRecording = async (tableId: number, enabled: boolean): Promise<void> => {
    await apiClient.patch(`/tables/${tableId}/video-recording`, { enabled })
}

export const openVideoScrub = async (tableId: number): Promise<import('../types/video.types').VideoScrubOpen> => {
    const response = await apiClient.post(`/tables/${tableId}/video/scrub/open`)
    return response.data.data
}

export const loadOlderVideoSegments = async (
    tableId: number,
    beforeSegmentId: number
): Promise<import('../types/video.types').VideoSegment[]> => {
    const response = await apiClient.get(`/tables/${tableId}/video/segments`, {
        params: { beforeSegmentId },
    })
    return response.data.data
}

export const buildVideoMediaUrl = (tableId: number, segmentId: number, accessToken: string): string => {
    const params = new URLSearchParams({ access_token: accessToken })
    return `/api/v1/tables/${tableId}/video/media/${segmentId}?${params.toString()}`
}

export const addVideoCharge = async (
    tableId: number,
    chargeType: 'FOUL_REVIEW' | 'CLIP_EDITING'
): Promise<import('../types/video.types').VideoChargeResult> => {
    const response = await apiClient.post(`/tables/${tableId}/video-charges`, { chargeType })
    return response.data.data
}

export const createVideoExport = async (
    tableId: number,
    body: import('../types/videoExport.types').CreateVideoExportRequest
): Promise<import('../types/videoExport.types').VideoExport> => {
    const response = await apiClient.post(`/tables/${tableId}/video/exports`, body)
    return response.data.data
}

export const getVideoExport = async (
    tableId: number,
    exportId: string
): Promise<import('../types/videoExport.types').VideoExport> => {
    const response = await apiClient.get(`/tables/${tableId}/video/exports/${exportId}`)
    return response.data.data
}

export const getExportDownloadLink = async (tableId: number, exportId: string): Promise<string> => {
    const response = await apiClient.get(`/tables/${tableId}/video/exports/${exportId}/download-link`)
    return response.data.data as string
}

export const pauseTable = async (
    tableId: number,
    mode: 'ACCRUED' | 'STAFF_MAX' = 'ACCRUED'
): Promise<void> => {
    const response = await apiClient.post(`/tables/${tableId}/pause`, null, {
        params: { mode },
    })
    return response.data.data
}

export const resumeTable = async (tableId: number): Promise<void> => {
    const response = await apiClient.post(`/tables/${tableId}/resume`)
    return response.data.data
}

export const resetHeartbeat = async (tableId: number): Promise<void> => {
    const response = await apiClient.post(`/tables/${tableId}/reset`)
    return response.data.data
}

export const fetchUnhealthyTables = async (): Promise<PoolTable[]> => {
    const response = await apiClient.get('/tables/health')
    return response.data.data
}